import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.researcher.discovery');
import { OnInit } from '@angular/core';
import { Service } from "src/libs/portal/season/service";

type DiscoveryTerm = {
    text: string;
    type: string;
    interest: string;
    active: boolean;
};

type DiscoveryWeight = {
    key: string;
    label: string;
    value: number;
    tone: string;
};

type DiscoveryRow = {
    id: string;
    researcherId: string;
    rank: number;
    name: string;
    nameEng: string;
    org: string;
    matchType: string;
    matchedPaperCount: number;
    totalCitedCount: number;
    firstAuthorCount: number;
    correspondingCount: number;
    leadAuthorCount: number;
    recentPaperCount: number;
    avgCitedPerPaper: number;
    totalPublications: number;
    topics: string[];
    score: number;
    serverScore: number;
    raw: any;
};

@Component({
    selector: 'wiz-page-researcher-discovery',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.researcher.discovery/view.scss */
:host {
  display: block;
}

.researcher-discovery-page {
  --korenet-brand: #4A6FD8;
  --korenet-brand-hover: #3D5DB8;
  --korenet-brand-soft: rgba(74, 111, 216, 0.1);
  font-family: "SUIT", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

.discovery-range {
  --range-percent: 50%;
  appearance: none;
  height: 6px;
  border-radius: 999px;
  background: linear-gradient(90deg, var(--korenet-brand) 0%, var(--korenet-brand) var(--range-percent), #e2e8f0 var(--range-percent), #e2e8f0 100%);
  outline: none;
}

.discovery-range::-webkit-slider-thumb {
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid #ffffff;
  background: var(--korenet-brand);
  box-shadow: 0 1px 5px rgba(15, 23, 42, 0.24);
  cursor: pointer;
}

.discovery-range::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border: 2px solid #ffffff;
  border-radius: 50%;
  background: var(--korenet-brand);
  box-shadow: 0 1px 5px rgba(15, 23, 42, 0.24);
  cursor: pointer;
}

.discovery-range:focus-visible::-webkit-slider-thumb {
  box-shadow: 0 0 0 4px rgba(74, 111, 216, 0.18);
}

.discovery-range:focus-visible::-moz-range-thumb {
  box-shadow: 0 0 0 4px rgba(74, 111, 216, 0.18);
}`],
})
export class PageResearcherDiscoveryComponent implements OnInit {
    public keyword: string = 'AI';
    public manualTerm: string = '';
    public loading: boolean = false;
    public searched: boolean = false;
    public expandOpen: boolean = true;
    public weightOpen: boolean = true;
    public total: number = 0;
    public cacheHit: boolean = false;
    public cacheStrategy: string = '';
    public searchStrategy: string = '';
    public searchError: string = '';
    public responseKeyword: string = '';

    public filters: any = {
        yearFrom: '',
        yearTo: '',
        minPapers: 1,
        topN: 20,
    };

    public expandedTerms: DiscoveryTerm[] = [];
    public rows: DiscoveryRow[] = [];
    public recentKeywords: string[] = ['AI', 'Battery', 'RNA', 'Deep Learning'];
    public popularKeywords: string[] = ['반도체', '인공지능', '이차전지', '바이오'];

    public weights: DiscoveryWeight[] = [
        { key: 'matchedPaperCount', label: '매칭 논문', value: 25, tone: 'blue' },
        { key: 'totalCitedCount', label: '총 피인용', value: 30, tone: 'emerald' },
        { key: 'leadAuthorCount', label: '제1저자', value: 10, tone: 'indigo' },
        { key: 'recentPaperCount', label: '최근5년', value: 10, tone: 'amber' },
        { key: 'avgCitedPerPaper', label: '평균 피인용', value: 25, tone: 'cyan' },
    ];

    private readonly defaultWeights: number[] = [25, 30, 10, 10, 25];
    private appliedWeightValues: { [key: string]: number } = {
        matchedPaperCount: 25,
        totalCitedCount: 30,
        leadAuthorCount: 10,
        recentPaperCount: 10,
        avgCitedPerPaper: 25,
    };
    private baseRows: DiscoveryRow[] = [];
    private uiRenderQueued: boolean = false;

    constructor(@Inject( Service) public service: Service) { }

    public async ngOnInit() {
        await this.service.init();
        const params = new URLSearchParams(location.search);
        this.keyword = (params.get('q') || this.keyword).trim() || 'AI';
        await this.runSearch();
    }

    public get activeTermCount(): number {
        return this.expandedTerms.filter((term: DiscoveryTerm) => term.active).length;
    }

    public get weightTotal(): number {
        return this.weights.reduce((total: number, weight: DiscoveryWeight) => total + Number(weight.value || 0), 0);
    }

    public get appliedWeightTotal(): number {
        return this.weights.reduce((total: number, weight: DiscoveryWeight) => total + this.appliedWeightByKey(weight.key), 0);
    }

    public get hasWeightChanges(): boolean {
        return this.weights.some((weight: DiscoveryWeight) => Number(weight.value || 0) !== this.appliedWeightByKey(weight.key));
    }

    public get effectiveTerms(): string[] {
        return this.expandedTerms
            .filter((term: DiscoveryTerm) => term.active)
            .map((term: DiscoveryTerm) => term.text);
    }

    public get visibleRows(): DiscoveryRow[] {
        const minMatches = Math.max(0, Number(this.filters.minPapers || 0));
        const topN = Math.max(1, Number(this.filters.topN || 20));
        return this.rows
            .filter((row: DiscoveryRow) => row.matchedPaperCount >= minMatches)
            .slice(0, topN);
    }

    public get candidateTotal(): number {
        return Math.max(Number(this.total || 0), this.rows.length);
    }

    public get scoreBasisText(): string {
        const total = this.appliedWeightTotal || 1;
        return this.weights
            .map((weight: DiscoveryWeight) => `${weight.label} ${Math.round((this.appliedWeightByKey(weight.key) / total) * 100)}%`)
            .join(' · ');
    }

    public get cacheStatusText(): string {
        if (!this.searched || this.loading || this.searchError) return '';
        const strategy = this.cacheStrategy || this.searchStrategy;
        if (this.cacheHit) {
            return strategy ? `공유 캐시 사용 · ${strategy}` : '공유 캐시 사용';
        }
        return strategy ? `캐시 생성/갱신 · ${strategy}` : '캐시 생성/갱신';
    }

    public async runSearch() {
        const nextKeyword = (this.keyword || '').trim();
        if (!nextKeyword) {
            this.searched = false;
            this.rows = [];
            this.baseRows = [];
            this.total = 0;
            this.searchError = '';
            await this.service.render();
            return;
        }

        this.keyword = nextKeyword;
        this.responseKeyword = nextKeyword;
        this.loading = true;
        this.searched = true;
        this.searchError = '';
        await this.service.render();

        try {
            const data = await this.requestKeywordSearch(nextKeyword);
            this.applySearchResponse(data, nextKeyword);
        } catch (e) {
            this.rows = [];
            this.baseRows = [];
            this.total = 0;
            this.cacheHit = false;
            this.cacheStrategy = '';
            this.searchStrategy = '';
            this.searchError = String(e?.message || e || '검색 결과를 불러오지 못했습니다.');
        }

        this.loading = false;
        await this.service.render();
    }

    public async selectKeyword(keyword: string) {
        this.keyword = keyword;
        await this.runSearch();
    }

    public onTopNChange() {
        this.queueUiRender();
    }

    public onFilterChange() {
        this.queueUiRender();
    }

    public toggleExpandPanel() {
        this.expandOpen = !this.expandOpen;
        this.queueUiRender();
    }

    public toggleWeightPanel() {
        this.weightOpen = !this.weightOpen;
        this.queueUiRender();
    }

    public toggleTerm(index: number) {
        const term = this.expandedTerms[index];
        if (!term) return;
        term.active = !term.active;
        this.queueUiRender();
    }

    public removeTerm(index: number, event?: Event) {
        if (event) event.stopPropagation();
        this.expandedTerms.splice(index, 1);
        this.queueUiRender();
    }

    public addManualTerm() {
        const text = (this.manualTerm || '').trim();
        if (!text) return;
        const exists = this.expandedTerms.some((term: DiscoveryTerm) => this.normalizeTerm(term.text) === this.normalizeTerm(text));
        if (!exists) {
            this.expandedTerms.push({ text, type: '직접', interest: '검토', active: true });
        }
        this.manualTerm = '';
        this.queueUiRender();
    }

    public onWeightChange(key: string, event: any) {
        const target = this.weights.find((weight: DiscoveryWeight) => weight.key === key);
        if (!target) return;
        target.value = Math.max(0, Math.min(100, Number(event?.target?.value || 0)));
        this.queueUiRender();
    }

    public async applyWeights() {
        this.syncAppliedWeights();
        await this.runSearch();
    }

    public resetWeights() {
        this.weights.forEach((weight: DiscoveryWeight, index: number) => {
            weight.value = this.defaultWeights[index];
        });
        this.queueUiRender();
    }

    public termChipClass(term: DiscoveryTerm): string {
        if (!term?.active) return 'border border-slate-200 bg-white text-slate-500';
        if (term.type === '원본') return 'border border-[#4A6FD8]/30 bg-[#EEF2FF] text-[#1E3A8A]';
        if (term.type === '직접') return 'border border-orange-200 bg-orange-50 text-orange-700';
        return 'border border-slate-200 bg-slate-100 text-slate-700';
    }

    public visibleTopics(row: DiscoveryRow): string[] {
        return (row?.topics || []).slice(0, 5);
    }

    public openResearcher(row: DiscoveryRow) {
        const researcherId = row?.researcherId || row?.id || '';
        if (!researcherId) return;
        const params = new URLSearchParams();
        const keywords = this.visibleTopics(row);
        if (keywords.length > 0) {
            params.set('keywords', keywords.join('|'));
        }
        const qs = params.toString();
        location.href = `/researcher/${researcherId}` + (qs ? `?${qs}` : '');
    }

    public openLegacyKeywordSearch() {
        const params = new URLSearchParams();
        const query = (this.keyword || '').trim();
        if (query) params.set('q', query);
        params.set('tab', 'keyword');
        location.href = `/search?${params.toString()}`;
    }

    private async requestKeywordSearch(keyword: string): Promise<any> {
        const params = new URLSearchParams();
        params.set('keyword', keyword);
        params.set('page', '1');
        params.set('dump', String(Math.max(200, Number(this.filters.topN || 20))));
        params.set('sort', 'match');
        params.set('order', 'DESC');
        params.set('fuzzy', 'true');
        params.set('cache_reset', 'false');
        params.set('discovery_sort', 'true');
        params.set('discovery_weights', JSON.stringify(this.appliedWeightValues));

        const activeTerms = this.effectiveTerms;
        if (activeTerms.length > 1) {
            params.set('expanded_keywords', JSON.stringify(activeTerms));
        }

        const response = await fetch(`/wiz/api/page.search/keyword_search?${params.toString()}`, {
            method: 'POST',
            credentials: 'include',
        });
        const json = await response.json();
        if (!response.ok || json?.code !== 200) {
            throw new Error(json?.data?.message || json?.message || 'keyword_search API failed');
        }
        return json.data || {};
    }

    private applySearchResponse(data: any, fallbackKeyword: string) {
        const searchKeyword = String(data?.search_keyword || fallbackKeyword || '').trim() || fallbackKeyword;
        const terms = this.uniqueTerms([searchKeyword, ...(data?.expanded_keywords || [])]);
        this.responseKeyword = searchKeyword;
        this.keyword = searchKeyword;
        this.expandedTerms = terms.map((term: string, index: number) => ({
            text: term,
            type: index === 0 ? '원본' : '확장',
            interest: index === 0 ? '핵심' : '확장',
            active: true,
        }));

        this.total = Number(data?.total || 0) || 0;
        this.cacheHit = !!data?.keyword_result_cache_hit;
        this.cacheStrategy = String(data?.keyword_result_cache_strategy || '');
        this.searchStrategy = String(data?.keyword_search_strategy || '');
        this.baseRows = (data?.rows || []).map((row: any, index: number) => this.mapApiRow(row, index));
        this.rows = this.baseRows.map((row: DiscoveryRow, index: number) => ({
            ...row,
            rank: index + 1,
            score: row.serverScore,
            topics: [...row.topics],
        }));
    }

    private mapApiRow(row: any, index: number): DiscoveryRow {
        const researcherId = String(row?.researcher_id || row?.id || '').trim();
        const topics = this.uniqueTerms([
            ...this.normalizeKeywordList(row?.primary_matched_keywords),
            ...this.normalizeKeywordList(row?.matched_keywords),
            ...this.normalizeKeywordList(row?.keyword_kor || row?.keyword),
            ...this.normalizeKeywordList(row?.keyword_eng),
        ]).slice(0, 10);
        const hasPrimary = this.hasPrimaryKeywordMetrics(row);
        const fallbackPaperCount = hasPrimary ? this.toNumber(row?.primary_keyword_paper_count) : this.toNumber(row?.keyword_paper_count);
        const matchedPaperCount = this.toNumber(row?.matched_paper_cnt) || fallbackPaperCount;
        const totalCitedCount = this.toNumber(row?.total_cited_count);
        const firstAuthorCount = this.toNumber(row?.first_author_cnt);
        const correspondingCount = this.toNumber(row?.corresponding_cnt);
        const leadAuthorCount = firstAuthorCount + correspondingCount;
        const recentPaperCount = this.toNumber(row?.recent_paper_cnt);
        const avgCitedPerPaper = this.toNumber(row?.avg_cited_per_paper)
            || (matchedPaperCount > 0 ? totalCitedCount / matchedPaperCount : 0);
        const totalPublications = this.toNumber(row?.total_publication_count)
            || this.toNumber(row?.paper_cnt) + this.toNumber(row?.patent_cnt) + this.toNumber(row?.report_cnt);
        const serverScore = this.toNumber(row?.discovery_relevance_score || row?.relevance_score);

        return {
            id: researcherId || `RD-${index + 1}`,
            researcherId,
            rank: index + 1,
            name: String(row?.name_kor || row?.name_eng || researcherId || '-').trim(),
            nameEng: String(row?.name_eng || '').trim(),
            org: String(row?.org_name || row?.org || '').trim(),
            matchType: hasPrimary ? 'primary' : (topics.length > 1 ? 'expanded' : 'keyword'),
            matchedPaperCount,
            totalCitedCount,
            firstAuthorCount,
            correspondingCount,
            leadAuthorCount,
            recentPaperCount,
            avgCitedPerPaper,
            totalPublications,
            topics,
            score: serverScore,
            serverScore,
            raw: row,
        };
    }

    private hasPrimaryKeywordMetrics(row: any): boolean {
        return this.toNumber(row?.primary_keyword_pub_count) > 0
            || this.toNumber(row?.primary_keyword_paper_count) > 0
            || this.toNumber(row?.primary_keyword_patent_count) > 0
            || this.toNumber(row?.primary_keyword_report_count) > 0;
    }

    private appliedWeightByKey(key: string): number {
        const value = Number(this.appliedWeightValues[key] || 0);
        return Number.isFinite(value) ? value : 0;
    }

    private syncAppliedWeights() {
        this.weights.forEach((weight: DiscoveryWeight) => {
            this.appliedWeightValues[weight.key] = Math.max(0, Math.min(100, Number(weight.value || 0)));
        });
    }

    private normalizeKeywordList(value: any): string[] {
        const values = Array.isArray(value) ? value : String(value || '').split(/[;,|]/);
        return this.uniqueTerms(values.map((item: any) => String(item || '').trim()).filter((item: string) => !!item));
    }

    private uniqueTerms(values: any[]): string[] {
        const seen = new Set<string>();
        const terms: string[] = [];
        (values || []).forEach((value: any) => {
            const text = String(value || '').trim();
            const key = this.normalizeTerm(text);
            if (!key || seen.has(key)) return;
            seen.add(key);
            terms.push(text);
        });
        return terms;
    }

    private toNumber(value: any): number {
        const number = Number(value || 0);
        return Number.isFinite(number) ? number : 0;
    }

    private normalizeTerm(value: string): string {
        return String(value || '').trim().toLowerCase();
    }

    private queueUiRender() {
        if (this.uiRenderQueued) return;
        this.uiRenderQueued = true;
        const render = async () => {
            this.uiRenderQueued = false;
            await this.service.render();
        };
        if (typeof requestAnimationFrame === 'function') {
            requestAnimationFrame(() => { render(); });
        } else {
            setTimeout(() => { render(); }, 0);
        }
    }
}

export default PageResearcherDiscoveryComponent;